import React from 'react'
import { t } from '@lingui/macro'
import { Alert, Box, Button, Typography, FormControl, Select, MenuItem, Chip, Skeleton } from '@mui/material'
import InboxIcon from '@mui/icons-material/Inbox'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { FormSubmissionStatus } from '@/core/models/form.model'
import { useEthicsSubmissions } from '../model/useEthicsSubmissions'
import { EthicsSubmissionsList } from './EthicsSubmissionsList'
import { RespondToEthicsSubmissionDialog } from './RespondToEthicsSubmissionDialog'
import { useEthicsDialogs } from '../model/useEthicsDialogs'
import { StatCard } from './StatCard'
import { SectionLabel } from './SectionLabel'
import { EthicsDetailDialog } from './EthicsDetailDialog'

export function EthicsPage() {
  const { submissions, totalCount, isError, isLoading, page, rowsPerPage, statusFilter, statusCounts, setPage, refetch, handleStatusFilterChange, handleRowsPerPageChange } =
    useEthicsSubmissions()

  const { activeDialog, selectedSubmission, openDetail, closeDialog, switchToRespond } = useEthicsDialogs()

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em', color: 'text.primary', mb: 1 }}>
          {t`Ethical Questions`}
        </Typography>
        <Box sx={{ height: 5, width: 56, bgcolor: 'primary.main', borderRadius: 4, mb: 2 }} />
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, lineHeight: 1.7 }}>
          {t`Review and respond to ethical questions submitted by patients in your establishment.`}
        </Typography>
      </Box>

      {/* Stats */}
      <Box sx={{ mb: 5 }}>
        <SectionLabel>
          {t`Overview`}
        </SectionLabel>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {isLoading ? (
            Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} variant="rounded" width={200} height={80} sx={{ borderRadius: 3, flex: 1 }} />)
          ) : (
            <>
              <StatCard icon={<InboxIcon />} label={t`Submitted`} value={statusCounts.submitted} muiColor="warning" />
              <StatCard icon={<CheckCircleIcon />} label={t`Resolved`} value={statusCounts.resolved} muiColor="success" />
            </>
          )}
        </Box>
      </Box>

      {/* Filter */}
      <Box sx={{ mb: 4 }}>
        <SectionLabel>
          {t`Filter by status`}
        </SectionLabel>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <Select
              value={statusFilter}
              displayEmpty
              onChange={(e) => handleStatusFilterChange(e.target.value as FormSubmissionStatus | '')}
              sx={{
                borderRadius: 3,
                bgcolor: 'background.idle',
                fontWeight: 500,
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: '2px solid',
                  borderColor: 'primary.light',
                },
              }}
            >
              <MenuItem value="">{t`All statuses`}</MenuItem>
              <MenuItem value={FormSubmissionStatus.Submitted}>{t`Submitted`}</MenuItem>
              <MenuItem value={FormSubmissionStatus.Resolved}>{t`Resolved`}</MenuItem>
            </Select>
          </FormControl>
          <Chip
            label={`${totalCount} ${totalCount === 1 ? t`question` : t`questions`}`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        </Box>
      </Box>

      {/* Table */}
      <Box sx={{ mb: 4 }}>
        <SectionLabel>
          {t`Questions`}
        </SectionLabel>
        {isError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={refetch}>
                {t`Retry`}
              </Button>
            }
          >
            {t`An error occurred`}
          </Alert>
        ) : isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={56} sx={{ borderRadius: 2 }} />
            ))}
          </Box>
        ) : (
          <EthicsSubmissionsList
            submissions={submissions}
            totalCount={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            onViewSubmission={openDetail}
          />
        )}
      </Box>

      {/* Detail Dialog */}
      {activeDialog === 'detail' && selectedSubmission && (
        <EthicsDetailDialog submission={selectedSubmission} onClose={closeDialog} onRespond={switchToRespond} />
      )}

      {/* Respond Dialog */}
      {activeDialog === 'respond' && selectedSubmission && <RespondToEthicsSubmissionDialog open onClose={closeDialog} submission={selectedSubmission} />}
    </Box>
  )
}

export default EthicsPage
